import { sandboxBaseForApp } from './sandboxOverride'

export const isValidURL = (url) => {
  if (!url) return false
  try {
    // eslint-disable-next-line no-new
    new URL(url)
    return true
  } catch (error) {
    return false
  }
}

export const EMBED_URL_STRATEGIES = {
  'docs.google.com': (url) => url.replace('/pub?', '/embed?'),
  'youtube.com/watch': (url) => {
    const videoId = url.match(/[?&]v=([^&]+)/)?.[1]
    return videoId ? `https://www.youtube.com/embed/${videoId}` : url
  },
  'youtu.be': (url) => {
    const videoId = url.match(/youtu\.be\/([^?]+)/)?.[1]
    return videoId ? `https://www.youtube.com/embed/${videoId}` : url
  },
  'vimeo.com': (url) => {
    const videoId = url.match(/vimeo\.com\/(?:video\/)?([^?&#]+)/)?.[1]
    return videoId ? `https://player.vimeo.com/video/${videoId}` : url
  },
  'miro.com/app/board/': (url) => {
    const boardId = url.match(/board\/([^?&#]+)/)?.[1]
    return boardId ? `https://miro.com/app/embed/${boardId}` : url
  },
  'skoletube.dk/video/': (url) => {
    const videoId = url.match(/video\/[^/]+\/([^/]+)/)?.[1]
    return videoId ? `https://skoletube.dk/e/${videoId}` : url
  },
}

export const getParamValue = (param, url) => {
  if (!url) url = window.location.href
  param = param.replace(/[\\[\]]/g, '\\$&')
  const regex = new RegExp(`[?&]${param}(=([^&#]*)|&|#|$)`)
  const results = regex.exec(url)
  if (!results) return null
  if (!results[2]) return ''
  return decodeURIComponent(results[2].replace(/\+/g, ' '))
}

export const resolveSameOriginBase = (url) => {
  if (!url || typeof window === 'undefined') return url
  const { origin } = window.location
  let path = null
  if (url.startsWith('/')) {
    path = url
  } else if (url === origin || url.startsWith(`${origin}/`)) {
    path = url.slice(origin.length) || '/'
  }
  if (path !== null) {
    const sandboxBase = sandboxBaseForApp(path.split('/')[1])
    if (sandboxBase) return origin + sandboxBase
  }
  return url.startsWith('/') ? origin + url : url
}

// Take one query param off a URL and leave everything else exactly as it was — the other params,
// their original encoding, and the fragment.
//
// The alternative, rebuilding the path from the router and letting replaceState have that, throws
// the rest of the URL away. That is fatal in a PowerPoint dialog or task pane: Office hands the
// frame its identity ON the URL — `_host_Info` in the query, `_xdm_Info` in the fragment — and a
// frame that loses them cannot finish the host handshake (office.js then fails its trust check
// and shows "we can't determine if the hosting page is trusted", or never fires onReady at all;
// see services/office's onReady catch).
//
// String surgery rather than URL/URLSearchParams because those re-serialise the query on the way
// out: URLSearchParams percent-encodes `$` and `|`, which is most of what `_host_Info`'s value is
// made of. Removing one param must not rewrite the ones it keeps.
//
// Returns the URL in the shape it was given — absolute in, absolute out — or unchanged when there
// is no query at all to take anything out of.
export const removeUrlParam = (param, url) => {
  const source = url || window.location.href
  const hashAt = source.indexOf('#')
  const hash = hashAt === -1 ? '' : source.slice(hashAt)
  const beforeHash = hashAt === -1 ? source : source.slice(0, hashAt)
  const queryAt = beforeHash.indexOf('?')
  if (queryAt === -1) return source

  const path = beforeHash.slice(0, queryAt)
  // A param with no `=` is still that param ("?new" means the same as "?new=") — match both, and
  // nothing that merely starts with the same letters ("newFoo=1" is a different param).
  const kept = beforeHash
    .slice(queryAt + 1)
    .split('&')
    .filter((pair) => pair !== param && pair.indexOf(`${param}=`) !== 0)

  return `${path}${kept.length ? `?${kept.join('&')}` : ''}${hash}`
}
